import { Injectable } from '@angular/core';
declare var THREE: any;
import { StageService } from './stage.service';
import { SizeHelperService } from './size-helper.service';
import { ConstantsService } from './constants.service';
import { BuildingCheckerService } from './building-checker.service';
import { ModelLoaderService } from './model-loader.service';
import { SetMaterialsService } from './set-materials.service';
import { LoadingSpinnerService } from './loading-spinner.service';
import { DataForDisplayingMenuItemsService } from './data-for-displaying-menu-items.service';
import { SceneLoadingService } from './scene-loading.service';

@Injectable()
export class StraightStairService {

  private intermediateStair: any;
  private bottomStair: any;
  private stairsPositions: any = [];
  private angle: any;
  private norm: any;
  firstOutPoint: any;
  secondOutPoint: any;

  constructor(
    private stage: StageService,
    private sizeHelper: SizeHelperService,
    private constants: ConstantsService,
    private buildingCheckerService: BuildingCheckerService,
    private modelLoaderService: ModelLoaderService,
    private setMaterialsService: SetMaterialsService,
    private loadingSpinnerService: LoadingSpinnerService,
    private dataForDisplayingMenuItemsService: DataForDisplayingMenuItemsService,
    private sceneLoadingService: SceneLoadingService
  ) {
  }

  build(config): Promise<any> {
    const intermediateStairPromise = this.modelLoaderService.loadLocalModel('Intermediate_step');
    const bottomStairPromise = this.modelLoaderService.loadLocalModel('open_staircase_bottom');
    const promises = [intermediateStairPromise, bottomStairPromise];
    this.loadingSpinnerService.spinnerLoadingListAdd('StraightStairService');
    return Promise.all(promises)
      .then(res => {
        this.loadingSpinnerService.spinnerLoadingListRemove('StraightStairService');
        this.intermediateStair = res[0];
        this.bottomStair = res[1];
        if (config.superstructureId) {
          const A = config.firstPoint;
          const B = config.secondPoint;
          const AB = new THREE.Vector3(B.x - A.x, 0, B.y - A.y);
          // so stair don't built outside edge
          if (config.distance > AB.length() - config.width / 2 - this.constants.minimalSpaceForStairsOnSIde) {
            config.distance = AB.length() - config.width / 2 - this.constants.minimalSpaceForStairsOnSIde;
          }
          // remove stair if not enought space on edge
          if (config.width + this.constants.minimalSpaceForStairsOnSIde * 2 > AB.length()) {
            config.firstPoint.superstructureType = null;
            config.firstPoint.distance = null;
            config.firstPoint.superstructureId = null;
            config.firstPoint.floorId = null;
            return;
          }
        }
        this.setPositionsForStairs(config);
        const stairs = this.addStairs(config);
        this.dataForDisplayingMenuItemsService.needRailingRebuildIfVisible.next();
        this.setMaterialsService.setMaterialForStairs();
        return stairs;
      });
  }

  private setPositionsForStairs(config) {
    const height = config.height;

    const A = config.firstPoint;
    const B = config.secondPoint;
    let rot: any = null;
    if (A.x >= B.x - 0.05 && A.x <= B.x + 0.05 && A.y > B.y) {
      rot = Math.PI * 3 / 2;
    }

    const AB = new THREE.Vector3(B.x - A.x, 0, B.y - A.y);
    const norm = new THREE.Vector3(AB.z, 0, -AB.x);

    const norm2 = norm.clone();
    const stairHeight = this.sizeHelper.INCHToCM(7);

    const down = new THREE.Vector3(0, -stairHeight, 0);
    const countIntermediateStairs = Math.floor(height / stairHeight);

    this.stairsPositions = [];
    const firstPosition = new THREE.Vector3(A.x, height, A.y);
    AB.setLength(config.distance);
    firstPosition.add(AB);

    for (let i = 0; i < countIntermediateStairs; i++) {
      this.stairsPositions[i] = new THREE.Vector3(firstPosition.x, height, firstPosition.z);

      norm.setLength(this.sizeHelper.INCHToCM(9));
      norm2.setLength(norm.length() / 2);
      this.stairsPositions[i].add(norm2);
      if (i !== countIntermediateStairs) {
        this.stairsPositions[i].add(down);
      }

      for (let j = 0; j < i; j++) {
        this.stairsPositions[i].add(norm);
        this.stairsPositions[i].add(down);
      }
    }

    this.firstOutPoint = this.stairsPositions[countIntermediateStairs - 1].clone();
    this.secondOutPoint = this.stairsPositions[countIntermediateStairs - 1].clone();
    this.firstOutPoint.add(norm2);
    this.secondOutPoint.add(norm2);
    let halfStairLengthVector = new THREE.Vector3(norm2.z, 0, -norm2.x);
    halfStairLengthVector.setLength(config.width / 2);
    this.firstOutPoint.add(halfStairLengthVector);
    halfStairLengthVector.negate();
    this.secondOutPoint.add(halfStairLengthVector);
    this.firstOutPoint.y = 0;
    this.secondOutPoint.y = 0;

    const med = new THREE.Vector3(B.x - A.x, 0, B.y - A.y);
    med.setLength(1);
    this.norm = med;

    if (rot) {
      this.norm.rot = rot;
    }

    const nullVec = new THREE.Vector3(-1, 0, 0);
    this.angle = med.angleTo(nullVec);

    if (med.x * nullVec.x + med.z * nullVec.z <= 0 && med.x * nullVec.z - med.z * nullVec.x < 0) {
      this.angle += Math.PI;
    }

  }

  private addStairs(config) {
    if (config.oldstair) {
      this.stage.remove(config.oldstair)
    }
    const stairs = new THREE.Object3D();
    stairs.userData.firstOutPoint = this.firstOutPoint;
    stairs.userData.secondOutPoint = this.secondOutPoint;

    const superstructureId = stairs.id;
    stairs.userData.type = 'Straight';

    // stairs.position.set(this.stairsPositions[0].x, this.stairsPositions[0].y, this.stairsPositions[0].z);
    stairs.name = 'stairs';
    stairs.userData.name = 'stairs';
    this.stage.add(stairs);

    stairs.userData.config = config;
    stairs.userData.config.superstructureType = 'straightStair';
    stairs.userData.config.superstructureId = superstructureId;

    config.firstPoint.distance = config.distance;
    config.firstPoint.superstructureType = 'straightStair';
    config.firstPoint.superstructureId = superstructureId;

    config.firstPoint.width = config.width;
    config.firstPoint.stairs = true;
    config.firstPoint.superstructure = stairs;

    for (let i = 0; i < this.stairsPositions.length - 1; i++) {
      let obj = this.intermediateStair.clone();

      obj.userData.isDeckComponent = true;
      obj.userData.isChildStep = true;

      obj.position.set(
        this.stairsPositions[i].x,
        this.stairsPositions[i].y - (this.constants.inermediateStepSize.length / 2),
        this.stairsPositions[i].z);
      obj.rotation.y = this.angle;
      obj.scale.x = config.width / this.constants.inermediateStepSize.width;

      let dir = obj.getWorldDirection(new THREE.Vector3());

      for (let j = 0; j < 4; j++) {
        let x = this.norm.x * dir.z - this.norm.z * dir.x;
        if (!(this.norm.x * dir.z - this.norm.z * dir.x > -0.05 &&
          this.norm.x * dir.z - this.norm.z * dir.x < 0.05 &&
          this.norm.x > -dir.x - 0.05 && this.norm.x < -dir.x + 0.05)) {
          obj.rotation.y += Math.PI / 2;
          dir = obj.getWorldDirection(new THREE.Vector3());
        }
      }

      let x = this.norm.x * dir.z - this.norm.z * dir.x;
      if (!(this.norm.x * dir.z - this.norm.z * dir.x > -0.05 &&
        this.norm.x * dir.z - this.norm.z * dir.x < 0.05 &&
        this.norm.x > -dir.x - 0.05 && this.norm.x < -dir.x + 0.05)) {
        obj.rotation.y = - obj.rotation.y;
        dir = obj.getWorldDirection(new THREE.Vector3());
      }

      for (let j = 0; j < 6; j++) {
        let x = this.norm.x * dir.z - this.norm.z * dir.x;
        if (!(this.norm.x * dir.z - this.norm.z * dir.x > -0.05 &&
          this.norm.x * dir.z - this.norm.z * dir.x < 0.05 &&
          this.norm.x > -dir.x - 0.05 && this.norm.x < -dir.x + 0.05)) {
          obj.rotation.y += Math.PI / 2;
          dir = obj.getWorldDirection(new THREE.Vector3());
        }
      }

      obj.rotation.y -= Math.PI / 2;
      if (this.norm.rot) {
        obj.rotation.y = this.norm.rot;
      }

      if (i == 0) {
        obj.name = "Top";
      }

      if (i == this.stairsPositions.length - 2) {
        obj.name = "SubBottom";
      }

      this.stage.add(obj);

      THREE.SceneUtils.attach(obj, this.stage.getScene(), stairs);
    }
    let i = this.stairsPositions.length - 1;
    let obj = this.bottomStair.clone();

    obj.userData.isDeckComponent = true;
    obj.userData.isChildStep = true;

    obj.position.set(this.stairsPositions[i].x, this.stairsPositions[i].y - 17.16 / 2, this.stairsPositions[i].z);;
    obj.rotation.y = this.angle;
    obj.scale.x = config.width / this.constants.inermediateStepSize.width;

    let dir = obj.getWorldDirection(new THREE.Vector3());

    for (let j = 0; j < 4; j++) {
      let x = this.norm.x * dir.z - this.norm.z * dir.x;
      if (!(this.norm.x * dir.z - this.norm.z * dir.x > -0.05 &&
        this.norm.x * dir.z - this.norm.z * dir.x < 0.05 &&
        this.norm.x > -dir.x - 0.05 && this.norm.x < -dir.x + 0.05)) {
        obj.rotation.y += Math.PI / 2;
        dir = obj.getWorldDirection(new THREE.Vector3());
      }
    }

    let x = this.norm.x * dir.z - this.norm.z * dir.x;
    if (!(
      this.norm.x * dir.z - this.norm.z * dir.x > -0.05 &&
      this.norm.x * dir.z - this.norm.z * dir.x < 0.05 &&
      this.norm.x > -dir.x - 0.05 && this.norm.x < -dir.x + 0.05
    )) {
      obj.rotation.y = - obj.rotation.y;
      dir = obj.getWorldDirection(new THREE.Vector3());
    }

    for (let j = 0; j < 6; j++) {
      let x = this.norm.x * dir.z - this.norm.z * dir.x;
      if (!(this.norm.x * dir.z - this.norm.z * dir.x > -0.05 &&
        this.norm.x * dir.z - this.norm.z * dir.x < 0.05 &&
        this.norm.x > -dir.x - 0.05 && this.norm.x < -dir.x + 0.05)) {
        obj.rotation.y += Math.PI / 2;
        dir = obj.getWorldDirection(new THREE.Vector3());
      }
    }

    obj.rotation.y -= Math.PI / 2;
    if (this.norm.rot) {
      obj.rotation.y = this.norm.rot;
    }

    obj.name = 'Bottom';

    this.stage.add(obj);
    THREE.SceneUtils.attach(obj, this.stage.getScene(), stairs);
    const floor = this.stage.getFloorById(config.floorId);
    if (!config.prevPoint) {
      const points = floor.userData.points;
      for (let i = 0; i < points.length; i++) {
        if (config.firstPoint === points[i]) {
          config.prevPoint = i > 0 ? points[i - 1] : points[points.length - 1];
          config.nextPoint = (i < points.length - 2) ? points[i + 2] : ((i === points.length - 2) ? points[0] : points[1]);
        }
      }
    }
    THREE.SceneUtils.attach(stairs, this.stage.getScene(), floor);
    
    // Уведомляем SceneLoadingService о создании ступенек
    setTimeout(() => {
      this.sceneLoadingService.forceCheck();
    }, 100);
    
    return stairs;
  }

  rescaleStairs(): void {
    const straightStairs = this.stage.getStairs().filter(obj =>
      obj.userData.config.superstructureType === 'straightStair');
    for (let i = 0; i < straightStairs.length; i++) {

      if (this.buildingCheckerService.checkHeightForStairs(
        'straightStair',
        straightStairs[i].parent.getWorldPosition(new THREE.Vector3()).y)
      ) {
        straightStairs[i].userData.config.height = straightStairs[i].parent.getWorldPosition(new THREE.Vector3()).y;
        straightStairs[i].parent.remove(straightStairs[i]);
        this.build(straightStairs[i].userData.config);
      }
      else {
        this.dataForDisplayingMenuItemsService.needStairRemove.next(straightStairs[i]);
      }
    }
  }

}



// WEBPACK FOOTER //
// ./src/app/services/straight-stairs.service.ts